import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { movementService } from "~/services/contabilidad/movementService";
import { useProfileStore } from "~/store/profileStore";
import type {
  MoveLineFormData,
  MovementFilters,
  MovementFormData,
} from "~/types/contabilidad";

export const MOVEMENTS_QUERY_KEY = ["movements"];
export const MOVE_LINES_QUERY_KEY = ["move-lines"];

// ============================================
// MOVEMENTS HOOKS
// ============================================

export function useMovements(filters?: MovementFilters) {
  return useQuery({
    queryKey: [...MOVEMENTS_QUERY_KEY, filters],
    queryFn: () => movementService.getAll(filters),
  });
}

export function useMovementsByCompany(companyId: string) {
  return useQuery({
    queryKey: [...MOVEMENTS_QUERY_KEY, "company", companyId],
    queryFn: () => movementService.getByCompany(companyId),
    enabled: !!companyId,
  });
}

export function useMovement(id: string) {
  return useQuery({
    queryKey: [...MOVEMENTS_QUERY_KEY, id],
    queryFn: () => movementService.getById(id),
    enabled: !!id,
  });
}

export function useMovementWithLines(id: string) {
  return useQuery({
    queryKey: [...MOVEMENTS_QUERY_KEY, id, "with-lines"],
    queryFn: () => movementService.getWithLines(id),
    enabled: !!id,
  });
}

export function useCreateMovement() {
  const queryClient = useQueryClient();
  const role = useProfileStore((state) => state.role);

  return useMutation({
    mutationFn: (data: MovementFormData) =>
      movementService.create(data, role ?? undefined),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: MOVEMENTS_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: [...MOVEMENTS_QUERY_KEY, "company", data.company_id],
      });
      toast.success("Movimiento creado exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al crear movimiento: ${error.message}`);
    },
  });
}

export function useUpdateMovement() {
  const queryClient = useQueryClient();
  const role = useProfileStore((state) => state.role);

  return useMutation({
    mutationFn: ({
      id,
      data,
      preserveApLines,
    }: {
      id: string;
      data: Partial<MovementFormData>;
      // Pago-a-proveedor drafts: preserve the locked AP (Proveedores) lines and
      // only replace the non-AP lines (see movementService.updatePreservingApLines).
      preserveApLines?: boolean;
    }) =>
      preserveApLines
        ? movementService.updatePreservingApLines(id, data, role ?? undefined)
        : movementService.update(id, data, role ?? undefined),
    onSuccess: (updatedMovement, { id }) => {
      queryClient.invalidateQueries({ queryKey: MOVEMENTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: [...MOVEMENTS_QUERY_KEY, id] });
      queryClient.invalidateQueries({
        queryKey: [
          ...MOVEMENTS_QUERY_KEY,
          "company",
          updatedMovement.company_id,
        ],
      });
      toast.success("Movimiento actualizado exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al actualizar movimiento: ${error.message}`);
    },
  });
}

export function usePublishMovement() {
  const queryClient = useQueryClient();
  const userId = useProfileStore((state) => state.id);

  return useMutation({
    mutationFn: (id: string) => movementService.publish(id, userId!),
    onSuccess: (updatedMovement, id) => {
      queryClient.invalidateQueries({ queryKey: MOVEMENTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: [...MOVEMENTS_QUERY_KEY, id] });
      queryClient.invalidateQueries({
        queryKey: [
          ...MOVEMENTS_QUERY_KEY,
          "company",
          updatedMovement.company_id,
        ],
      });
      toast.success("Movimiento publicado exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al publicar movimiento: ${error.message}`);
    },
  });
}

export function useUnpublishMovement() {
  const queryClient = useQueryClient();
  const userId = useProfileStore((state) => state.id);
  const role = useProfileStore((state) => state.role);

  return useMutation({
    mutationFn: (id: string) => movementService.unpublish(id, userId!, role!),
    onSuccess: (updatedMovement, id) => {
      queryClient.invalidateQueries({ queryKey: MOVEMENTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: [...MOVEMENTS_QUERY_KEY, id] });
      queryClient.invalidateQueries({
        queryKey: [
          ...MOVEMENTS_QUERY_KEY,
          "company",
          updatedMovement.company_id,
        ],
      });
      toast.success("Movimiento despublicado exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al despublicar movimiento: ${error.message}`);
    },
  });
}

export function useDeleteMovement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => movementService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MOVEMENTS_QUERY_KEY });
      toast.success("Movimiento eliminado exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al eliminar movimiento: ${error.message}`);
    },
  });
}

// ============================================
// MOVEMENT LINES HOOKS
// ============================================

export function useMoveLines(moveId: string) {
  return useQuery({
    queryKey: [...MOVE_LINES_QUERY_KEY, moveId],
    queryFn: () => movementService.getLines(moveId),
    enabled: !!moveId,
  });
}

export function useCreateMoveLine() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: MoveLineFormData) => movementService.createLine(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...MOVE_LINES_QUERY_KEY, variables.move_id],
      });
      queryClient.invalidateQueries({
        queryKey: [...MOVEMENTS_QUERY_KEY, variables.move_id, "with-lines"],
      });
      toast.success("Línea agregada exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al agregar línea: ${error.message}`);
    },
  });
}

export function useUpdateMoveLine() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<MoveLineFormData>;
    }) => movementService.updateLine(id, data),
    onSuccess: (updatedLine) => {
      queryClient.invalidateQueries({
        queryKey: [...MOVE_LINES_QUERY_KEY, updatedLine.move_id],
      });
      queryClient.invalidateQueries({
        queryKey: [...MOVEMENTS_QUERY_KEY, updatedLine.move_id, "with-lines"],
      });
      toast.success("Línea actualizada exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al actualizar línea: ${error.message}`);
    },
  });
}

export function useDeleteMoveLine() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, moveId }: { id: string; moveId: string }) =>
      movementService.deleteLine(id).then(() => moveId),
    onSuccess: (moveId) => {
      queryClient.invalidateQueries({
        queryKey: [...MOVE_LINES_QUERY_KEY, moveId],
      });
      queryClient.invalidateQueries({
        queryKey: [...MOVEMENTS_QUERY_KEY, moveId, "with-lines"],
      });
      toast.success("Línea eliminada exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al eliminar línea: ${error.message}`);
    },
  });
}

export function useCreateMoveLines() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (lines: MoveLineFormData[]) =>
      movementService.createLines(lines),
    onSuccess: (_, variables) => {
      if (variables.length > 0) {
        const moveId = variables[0].move_id;
        queryClient.invalidateQueries({
          queryKey: [...MOVE_LINES_QUERY_KEY, moveId],
        });
        queryClient.invalidateQueries({
          queryKey: [...MOVEMENTS_QUERY_KEY, moveId, "with-lines"],
        });
      }
      toast.success("Líneas agregadas exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al agregar líneas: ${error.message}`);
    },
  });
}
